// src/components/SharingStatus.tsx
import { useState } from "react";
import { Shield, Lock, Globe, Users, Building, Users2, Edit2, QrCode } from "lucide-react";
import type { AccessControl } from '@/lib/firebase';
import { getAllGroups, predefinedGroups } from '@/lib/groups';
import { SharingStatusDialog } from "@/components/SharingStatusDialog";
import { QRCodeDialog } from "@/components/QRCodeDialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SharingStatusProps {
  accessControl?: AccessControl;
  className?: string;
  userDomain?: string;
  isEditable?: boolean;
  onAccessChange?: (accessControl: AccessControl) => void;
  assistantId?: string;
  assistantName?: string;
}

export function SharingStatus({ 
  accessControl, 
  className = "", 
  userDomain = "",
  isEditable = false,
  onAccessChange,
  assistantId,
  assistantName
}: SharingStatusProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  
  if (!accessControl) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        <span className="text-xs sm:text-sm font-medium text-muted-foreground">Unknown</span>
        <span className="text-xs sm:text-sm text-muted-foreground/80 hidden sm:inline">• Unknown sharing status</span>
      </div>
    );
  }

  // Determine status based on access control type
  let Icon;
  let text;
  let description;

  switch (accessControl.type) {
    case 'private':
      Icon = Lock;
      text = 'Private';
      description = 'You';
      break;

    case 'public':
      Icon = Globe;
      text = 'Public';
      description = 'Anyone';
      break;

    case 'domain':
      Icon = Building;
      const domain = accessControl.domain || 'your organization';
      text = 'Domain';
      description = `${domain}`;
      break;

    case 'domains':
      Icon = Building;
      const count = accessControl.domains?.length || 0;
      text = 'Domains';
      description = `${count} domain${count !== 1 ? 's' : ''}`;
      
      // Optional: List domains if there are a reasonable number
      if (accessControl.domains && accessControl.domains.length > 0 && accessControl.domains.length <= 2) {
        description += `: ${accessControl.domains.join(', ')}`;
      }
      break;

    case 'specific':
      Icon = Users;
      const emailCount = accessControl.emails?.length || 0;
      text = 'Specific Users';
      description = `${emailCount} user${emailCount !== 1 ? 's' : ''}`;
      break;

    case 'group':
      Icon = Users2;
      const groupCount = accessControl.groups?.length || 0;
      text = 'Groups';
      description = `${groupCount} group${groupCount !== 1 ? 's' : ''}`;
      
      // Show group names if available
      if (accessControl.groups && accessControl.groups.length > 0) {
        // Try to get groups from both imports or use a direct reference
        let availableGroups = [];
        
        try {
          // Try the exported getAllGroups function
          availableGroups = getAllGroups();
        } catch (error) {
          console.error("Error getting groups:", error);
          
          // Fallback to the directly imported predefinedGroups
          try {
            availableGroups = predefinedGroups;
          } catch (error) {
            console.error("Error using predefinedGroups:", error);
            
            // Last resort fallback - hardcoded map of group IDs to names
            availableGroups = [
              { id: 'alicante', name: 'Alicante Team' },
              { id: 'dev-team', name: 'Development Team' },
              { id: 'external', name: 'External Users' },
              { id: 'engineering-team', name: 'Engineering Team' },
              { id: 'marketing-team', name: 'Marketing Team' },
              { id: 'leadership', name: 'Leadership' }
            ];
          }
        }
        
        // Map group IDs to their names
        const groupNames = accessControl.groups
          .map(groupId => {
            const group = availableGroups.find(g => g.id === groupId);
            return group?.name || groupId;
          });
        
        // Add group names to description if there are a reasonable number, limit display to 2 for mobile
        if (groupNames.length <= 2) {
          description += `: ${groupNames.join(', ')}`;
        }
      }
      break;

    default:
      Icon = Shield;
      text = 'Custom';
      description = 'Custom access settings';
  }

  const handleClick = () => {
    if (isEditable && onAccessChange) {
      setDialogOpen(true);
    }
  };

  // Determine if component should be clickable
  const isClickable = isEditable && !!onAccessChange;
  const cursorClass = isClickable ? 'cursor-pointer hover:bg-muted/40 transition-colors' : '';
  const paddingClass = isClickable ? 'px-2 py-1 rounded-md' : '';

  const statusComponent = (
    <div 
      className={`inline-flex items-center gap-2 ${className} ${cursorClass} ${paddingClass} group max-w-full`}
      onClick={handleClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="flex items-center gap-1.5 shrink-0">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        <span className="text-xs sm:text-sm font-medium text-muted-foreground whitespace-nowrap">{text}</span>
      </div>
      
      {/* Desktop description - only shown if there's enough space */}
      <div className="hidden sm:flex sm:items-center max-w-[200px] lg:max-w-xs">
        <span className="text-xs sm:text-sm text-muted-foreground/80 whitespace-nowrap overflow-hidden text-ellipsis">
          • {description}
        </span>
      </div>
      
      {/* Mobile description - stays inline and truncates */}
      <div className="sm:hidden flex-1 min-w-0">
        <span className="text-xs text-muted-foreground/80 overflow-hidden text-ellipsis whitespace-nowrap block">
          • {description}
        </span>
      </div>
      
      {isClickable && (
        <Edit2 className="h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-muted-foreground/80 transition-colors shrink-0" />
      )}
    </div>
  );

  return (
    <>
      <div className="flex items-center gap-2">
        {isClickable ? (
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                {statusComponent}
              </TooltipTrigger>
              <TooltipContent>
                Click to edit sharing settings
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          statusComponent
        )}

        {/* QR Code Button - show for public/shared assistants */}
        {assistantId && assistantName && accessControl.type !== 'private' && (
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQrDialogOpen(true)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                >
                  <QrCode className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Show QR code for sharing
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {isEditable && onAccessChange && (
        <SharingStatusDialog
          isOpen={dialogOpen}
          onOpenChange={setDialogOpen}
          accessControl={accessControl}
          onAccessChange={onAccessChange}
          userDomain={userDomain}
        />
      )}

      {/* QR Code Dialog */}
      {assistantId && assistantName && (
        <QRCodeDialog
          isOpen={qrDialogOpen}
          onOpenChange={setQrDialogOpen}
          assistantId={assistantId}
          assistantName={assistantName}
        />
      )}
    </>
  );
}